import { ReferralEventDataObj } from '@/utils/referral.eventparams';
import { trackPixelEvent } from '@/utils/utils';
import { trackGaEvent, trackMoengageEvent } from '.';
import { getBatchTransactionSuccessEventName } from './event-utils';
import { initMoengage } from './helper';

export enum EventType {
  'MARKET_PAGE_VISIT' = 'market_page_visit',
  'CLASS_FILTER_SELECT' = 'class_filter_select',
  'OTP_SENT' = 'otp_sent',
  'OTP_RESEND_CLICK' = 'otp_resend_click',
  'MARKET_BOOK_SEAT' = 'market_book_seat',
  'SIGNUP_SUCCESS' = 'signup_success',
  'LOGIN_SUCCESS' = 'login_success',
  'MARKET_ENROLL_CLICK' = 'market_enroll_click',
  'MWEB_ENROLL_CLICK' = 'mweb_enroll_click',
  'SIGNIN_SUCCESS' = 'signin_success',
  'BATCH_ENROLL_FREE' = 'batch_enroll_free',
  'ON_PAYMENT_CANCEL' = 'on_payment_cancel',
  'TRANSACTION_SUCCESS' = 'transaction_success',
  'TRANSACTION_SUCCESS_MASTER' = 'master_transaction_success',
  'TRANSACTION_SUCCESS_BOOSTER' = 'booster_transaction_success',
  'TRANSACTION_SUCCESS_MEGA' = 'mega_transaction_success',
  'TRANSACTION_SUCCESS_POWER' = 'power_transaction_success',
  'TRANSACTION_SUCCESS_POCKET' = 'pocket_transaction_success',
  'TRANSACTION_FAILED' = 'transaction_failed',
  'TRANSACTION_CANCEL' = 'transaction_cancel',
  'ERROR_IN_PURCHASE' = 'error_in_purchase',
  'CURRICULAM_VIEW_MORE' = 'curriculum_view_more',
  'CURRICULAM_READ_MORE' = 'curriculum_read_more',
  'CLASS_PAGE_VISIT' = 'class_page_visit',
  'CURRICULAM_CLASS_SELECT' = 'curriculum_class_select',
  'COURSES_FAQ_CLICK' = 'course_faq_click',
  'CLASSES_FAQ_CLICK' = 'classes_faq_click',
  'HEADER_ITEM_CLICK' = 'header_item_click',
  'COURSE_PAGE_VISIT' = 'course_page_visit',
}

export type MLP_TYPE =
  | 'static'
  | 'video_mlp'
  | 'video_header'
  | 'horizontal_image'
  | 'horizontal_video'
  | 'landing';

export const eventTracker = {
  marketPageVisit: async (eventData: {
    page_id: string;
    page_link: string;
    default_amount?: number;
    mlp_type: MLP_TYPE;
    referral_mlp?: string;
    referrer_code?: string;
    referrer_name?: string;
  }) => {
    const referralParams = await ReferralEventDataObj.getReferralEventData();
    if (referralParams) {
      eventData = {
        ...eventData,
        ...referralParams,
      };
    }
    trackGaEvent({
      eventName: EventType.MARKET_PAGE_VISIT,
      eventParams: eventData,
    });
    trackMoengageEvent({
      eventName: EventType.MARKET_PAGE_VISIT,
      eventParams: {
        ...eventData,
        is_user_status_required: true,
      },
    });
  },
  classFilterSelect: (eventData: {
    page?: 'landing' | 'course_page' | 'class_page';
    class: string;
    page_id?: string;
    mlp_type?: MLP_TYPE;
    from?: string;
    user_id?: string;
    user_name?: string;
    user_class?: string;
    source?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.CLASS_FILTER_SELECT,
      eventParams: {
        page: 'market',
        from: 'form',
        ...eventData,
      },
    });
    trackMoengageEvent({
      eventName: EventType.CLASS_FILTER_SELECT,
      eventParams: {
        page: 'market',
        from: 'form',
        ...eventData,
        is_user_status_required: true,
      },
    });
    trackPixelEvent(EventType.CLASS_FILTER_SELECT, {
      eventParams: {
        ...eventData,
      },
    });
  },
  otpSent: (eventData: {
    came_from?: 'form' | 'login' | 'market' | 'blp' | 'landing_page' | 'course_page' | 'class_page';
    user_type: 'new' | 'old';
    user_class: string;
    amount: number;
    mlp_type?: MLP_TYPE;
    view: 'otpless_view' | 'old';
    source?: string;
    page_id?: string;
    page_link?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.OTP_SENT,
      eventParams: {
        ...eventData,
      },
    });
    trackMoengageEvent({
      eventName: EventType.OTP_SENT,
      eventParams: {
        ...eventData,
      },
    });
    trackPixelEvent(EventType.OTP_SENT, {
      eventParams: {
        ...eventData,
      },
    });
  },
  otpResendClick: (eventData: {
    user_type: 'old' | 'new';
    user_class: string;
    amount: number;
    mlp_type?: MLP_TYPE;
    came_from: 'landing_page' | 'market';
    page_id?: string;
    view: 'otpless_view' | 'old';
    channel: 'SMS' | 'WHATSAPP';
  }) => {
    trackGaEvent({
      eventName: EventType.OTP_RESEND_CLICK,
      eventParams: {
        ...eventData,
        page: 'market',
        ...(eventData?.came_from ? { came_from: eventData?.came_from } : {}),
      },
    });
    trackMoengageEvent({
      eventName: EventType.OTP_RESEND_CLICK,
      eventParams: {
        ...eventData,
        page: 'market',
        ...(eventData?.came_from ? { came_from: eventData?.came_from } : {}),
      },
    });
  },
  marketBookSeat: (eventData: {
    page_id: string;
    user_class: string;
    amount: number;
    batch_id: string;
    mlp_type: MLP_TYPE;
  }) => {
    trackGaEvent({
      eventName: EventType.MARKET_BOOK_SEAT,
      eventParams: {
        ...eventData,
        page: 'market',
      },
    });
  },
  marketEnrollClick: (eventParams: {
    page_id: string;
    page_link: string;
    default_amount: number;
    mlp_type: MLP_TYPE;
    referrer_code?: string;
    referrer_name?: string;
    referral_mlp?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.MARKET_ENROLL_CLICK,
      eventParams,
    });
    trackMoengageEvent({
      eventName: EventType.MARKET_ENROLL_CLICK,
      eventParams: {
        ...eventParams,
        is_user_status_required: true,
      },
    });
  },
  mwebEnrollClick: (eventParams: {
    page_id: string;
    amount: number;
    page_link: string;
    mlp_type: MLP_TYPE;
    user_cohort?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    gclid?: string;
    fbclid?: string;
    igshid?: string;
    referrer_code?: string;
    referrer_name?: string;
    referral_mlp?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.MWEB_ENROLL_CLICK,
      eventParams: {
        page_id: eventParams.page_id,
        amount: eventParams.amount,
        page_link: eventParams.page_link,
        mlp_type: eventParams.mlp_type,
        page: 'mlp',
        referrer_code: eventParams.referrer_code,
        referrer_name: eventParams.referrer_name,
        referral_mlp: eventParams.referral_mlp,
      },
    });
    trackMoengageEvent({
      eventName: EventType.MWEB_ENROLL_CLICK,
      eventParams: {
        page_id: eventParams.page_id,
        amount: eventParams.amount,
        page_link: eventParams.page_link,
        mlp_type: eventParams.mlp_type,
        page: 'mlp',
        is_user_status_required: true,
      },
    });
    trackPixelEvent(EventType.MWEB_ENROLL_CLICK, {
      eventParams: {
        ...eventParams,
      },
    });
  },
  signupSuccess: async (eventParams: {
    user_id: string;
    user_class: string;
    page_id?: string;
    user_cohort: string;
    selected_cohort: string;
    mlp_type?: MLP_TYPE;
    came_from?: 'landing_page' | 'course_page' | 'class_page' | 'market';
    view: 'otpless_view' | 'old';
    page_link?: string;
    referrer_code?: string;
    referrer_name?: string;
    referral_mlp?: string;
    login_mode: 'otp' | 'truecaller';
    source?: string;
    platform?: string;
    user_name?: string;
    user_country?: string;
    user_phone?: string;
    country_code?: string;
    user_created_at?: string;
  }) => {
    initMoengage();

    const referralParams = await ReferralEventDataObj.getReferralEventData();
    if (referralParams) {
      eventParams = {
        ...eventParams,
        ...referralParams,
      };
    }

    trackGaEvent({
      eventName: EventType.SIGNUP_SUCCESS,
      eventParams: {
        came_from: eventParams?.came_from || 'market',
        user_id: eventParams.user_id || '',
        user_class: eventParams.user_class || '',
        page_id: eventParams.page_id || '',
        mlp_type: eventParams.mlp_type,
        view: eventParams.view || '',
        referrer_code: eventParams.referrer_code || '',
        referrer_name: eventParams.referrer_name || '',
        referral_mlp: eventParams.referral_mlp || '',
        login_mode: eventParams.login_mode,
        source: eventParams.source || 'web',
      },
    });

    trackMoengageEvent({
      eventName: EventType.SIGNUP_SUCCESS,
      eventParams: {
        ...eventParams,
        came_from: eventParams?.came_from || 'market',
        page_id: eventParams.page_id || '',
        mlp_type: eventParams.mlp_type,
        source: eventParams.source || 'web',
        platform: eventParams.platform || 'web',
        flow: 'revamped',
      },
    });
  },
  loginSuccess: (eventData: {
    page_id?: string;
    mlp_type?: MLP_TYPE;
    came_from?: 'landing_page' | 'market' | 'course_page' | 'class_page' | '';
    view: 'otpless_view' | 'old';
    user_id?: string;
    user_name?: string;
    user_cohort?: string;
    user_class?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    gclid?: string;
    fbclid?: string;
    igshid?: string;
    source?: string;
    page_link?: string;
    referrer_code?: string;
    referrer_name?: string;
    referral_mlp?: string;
    login_mode: 'otp' | 'truecaller';
    user_type?: 'old' | 'new';
    flow?: string;
  }) => {
    initMoengage();

    const came_from = eventData?.came_from || 'market';

    trackGaEvent({
      eventName: EventType.LOGIN_SUCCESS,
      eventParams: {
        ...eventData,
        came_from,
        source: eventData.source || 'web',
      },
    });

    trackMoengageEvent({
      eventName: EventType.SIGNIN_SUCCESS,
      eventParams: {
        ...eventData,
        came_from,
        selected_cohort: '',
        source: eventData.source || 'web',
        flow: eventData.flow || 'revamped',
      },
    });

    trackPixelEvent(EventType.LOGIN_SUCCESS, {
      eventParams: {
        ...eventData,
        came_from,
      },
    });
  },
  batchEnrollFree: (eventData: {
    page_id: string;
    batch_id: string;
    batch_name: string;
    batch_country_name: string;
    batch_type: string;
    cohort_id: string;
    mlp_type?: MLP_TYPE;
    type?: string;
    user_id?: string;
    pw_user_id?: string;
    user_cohort?: string;
    user_class?: string;
    user_name?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    gclid?: string;
    fbclid?: string;
    igshid?: string;
    source?: string;
    came_from?: string;
    page_link?: string;
  }) => {
    const limitedParams = {
      page_id: eventData.page_id,
      batch_id: eventData.batch_id,
      batch_name: eventData.batch_name,
      batch_country_name: eventData.batch_country_name,
      batch_type: eventData.batch_type,
      cohort_id: eventData.cohort_id,
      type: eventData.type,
    };

    trackGaEvent({
      eventName: EventType.BATCH_ENROLL_FREE,
      eventParams: {
        ...limitedParams,
        came_from: eventData.came_from || 'market',
      },
    });
    trackMoengageEvent({
      eventName: EventType.BATCH_ENROLL_FREE,
      eventParams: {
        ...limitedParams,
        came_from: eventData.came_from || 'market',
        selected_cohort: '',
      },
    });
    trackPixelEvent(EventType.BATCH_ENROLL_FREE, {
      eventParams: {
        ...eventData,
      },
    });
  },
  onPaymentCancel: async (eventParams: {
    page_id?: string;
    batch_id: string;
    batch_type: string;
    batch_name: string;
    batch_class?: string;
    batch_tagged_classes?: string;
    sub_total: string;
    discount: string;
    total_amount: string;
    came_from: string;
    selected_cohort: string;
    mlp_type?: MLP_TYPE;
    page_link?: string;
    from?: string;
    triggered_from?: string;
    experiment_name?: string;
  }) => {
    const data = {
      batch_id: eventParams.batch_id,
      batch_type: eventParams.batch_type,
      batch_name: eventParams.batch_name,
      batch_class: eventParams.batch_class,
      batch_tagged_classes: eventParams?.batch_tagged_classes,
      sub_total: eventParams.sub_total,
      discount: eventParams.discount,
      total_amount: eventParams.total_amount,
      came_from: eventParams.came_from,
      is_user_status_required: true,
      is_paid_batch_type_required: true,
      mlp_type: eventParams.mlp_type,
      selected_cohort: eventParams.selected_cohort,
    };
    trackGaEvent({
      eventName: EventType.ON_PAYMENT_CANCEL,
      eventParams: data,
    });

    trackMoengageEvent({
      eventName: EventType.TRANSACTION_CANCEL,
      eventParams: data,
    });
  },
  transactionSuccess: (eventParams: {
    page_id?: string;
    batch_id: string;
    batch_name: string;
    order_id: string;
    sub_total: string;
    discount: string;
    total_amount: string;
    batch_type: string;
    came_from: string;
    user_class?: string;
    coupon_code: string;
    mlp_type?: MLP_TYPE;
    page_link?: string;
    selected_cohort?: string;
    triggered_from?: string;
    experiment_name?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.TRANSACTION_SUCCESS,
      eventParams: {
        ...eventParams,
        is_user_status_required: true,
        is_paid_batch_type_required: true,
      },
    });

    trackMoengageEvent({
      eventName: EventType.TRANSACTION_SUCCESS,
      eventParams,
    });

    const eventName = getBatchTransactionSuccessEventName(eventParams?.batch_type);

    const payload: any = {
      page_id: eventParams?.page_id,
      batch_id: eventParams?.batch_id,
      batch_name: eventParams?.batch_name,
      batch_type: eventParams?.batch_type,
      order_id: eventParams?.order_id,
      sub_total: eventParams?.sub_total,
      discount: eventParams?.discount,
      total_amount: eventParams?.total_amount,
      came_from: eventParams?.came_from,
      coupon_code: eventParams?.coupon_code,
      mlp_type: eventParams?.mlp_type,
      page_link: eventParams?.page_link,
      selected_cohort: eventParams?.selected_cohort,
      triggered_from: eventParams?.triggered_from,
      experiment_name: eventParams?.experiment_name,
    };

    if (eventParams?.user_class) {
      payload.user_class = eventParams.user_class;
    }

    trackGaEvent({
      eventName: eventName,
      eventParams: {
        ...payload,
        is_user_status_required: true,
        is_paid_batch_type_required: true,
      },
    });

    trackMoengageEvent({
      eventName,
      eventParams: payload,
    });
  },
  transactionFailed: (eventParams: {
    batch_id: string;
    batch_name: string;
    batch_class: string;
    batch_tagged_classes: string;
    order_id: string;
    sub_total: string;
    discount: string;
    total_amount: string;
    batch_type: string;
    came_from: string;
    page_id?: string;
    mlp_type?: MLP_TYPE;
    selected_cohort: string;
    default_batch?: string;
    triggered_from?: string;
    experiment_name?: string;
    tagged_booster_batch_id?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.TRANSACTION_FAILED,
      eventParams: {
        ...eventParams,
        is_user_status_required: true,
        is_paid_batch_type_required: true,
      },
    });

    trackMoengageEvent({
      eventName: EventType.TRANSACTION_FAILED,
      eventParams,
    });
  },
  errorInPurchase: (eventParams: {
    came_from: string;
    mlp_type?: MLP_TYPE;
    error: string;
    batch_id?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.ERROR_IN_PURCHASE,
      eventParams,
    });
    trackMoengageEvent({
      eventName: EventType.ERROR_IN_PURCHASE,
      eventParams,
    });
  },
  curriculamViewMore: (eventParams: {
    page_name: string;
    page_link: string;
    curriculum_class: string;
  }) => {
    trackGaEvent({
      eventName: EventType.CURRICULAM_VIEW_MORE,
      eventParams: {
        ...eventParams,
      },
    });
  },
  curriculamReadMore: (eventParams: { page_name: string; page_link: string }) => {
    trackGaEvent({
      eventName: EventType.CURRICULAM_READ_MORE,
      eventParams: {
        ...eventParams,
      },
    });
  },
  classPageVisit: (eventParams: { page_name: string; page_link: string }) => {
    trackGaEvent({
      eventName: EventType.CLASS_PAGE_VISIT,
      eventParams: {
        ...eventParams,
      },
    });
  },
  curriculamClassSelect: (eventParams: {
    page_name: string;
    page_link: string;
    curriculum_class: string;
    came_from: string;
  }) => {
    trackGaEvent({
      eventName: EventType.CURRICULAM_CLASS_SELECT,
      eventParams: {
        ...eventParams,
      },
    });
  },
  faqClicked: (eventParams: {
    page_name: string;
    page_link: string;
    faq_question?: string;
    faq_answer?: string;
    came_from?: string;
  }) => {
    trackGaEvent({
      eventName:
        eventParams.came_from === 'course_page'
          ? EventType.COURSES_FAQ_CLICK
          : EventType.CLASSES_FAQ_CLICK,
      eventParams: {
        page_name: eventParams.page_name,
        page_link: eventParams.page_link,
        faq_question: eventParams.faq_question,
        faq_answer: eventParams.faq_answer,
      },
    });
  },
  headerItemClick: (eventParams: { item_name: string; page_link: string }) => {
    trackGaEvent({
      eventName: EventType.HEADER_ITEM_CLICK,
      eventParams: {
        ...eventParams,
      },
    });
  },
  coursePageVisit: (eventParams: { page_name: string; page_link: string; came_from?: string }) => {
    trackGaEvent({
      eventName: EventType.COURSE_PAGE_VISIT,
      eventParams: {
        ...eventParams,
      },
    });
  },
};
